import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ExitToApp as LogoutIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import VideoPlayer from '@components/VideoPlayer';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { CORE_SPORTS, isShootingSport } from '@utils/sports';

interface RankingStrokeType {
  id?: string;
  code: string;
  courtPosition?: string;
  halfSide?: string;
  strokeType?: string;
  strokeAction?: string;
  playType?: string;
  shotType?: string;
  placement?: string;
  difficulty?: string | number; // Can be string (legacy) or number (1-5 for star rating)
  deception?: string;
  returnResponse?: string;
  referenceVideos?: string;
  isActive?: boolean;
  displayOrder?: number;
}

const AdminRankingStrokeTypesManagement: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [strokeTypes, setStrokeTypes] = useState<RankingStrokeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editingStrokeType, setEditingStrokeType] = useState<RankingStrokeType | null>(null);
  const [formData, setFormData] = useState<RankingStrokeType>({
    code: '',
    courtPosition: '',
    halfSide: '',
    strokeType: '',
    strokeAction: '',
    playType: '',
    shotType: '',
    placement: '',
        difficulty: 0,
        deception: '',
    returnResponse: '',
    referenceVideos: '',
    isActive: true,
    displayOrder: 0,
  });
  const [videoUrls, setVideoUrls] = useState<string[]>(['']);
  const [selectedSport, setSelectedSport] = useState<string>('Badminton');
  const isFootball = isShootingSport(selectedSport);

  useEffect(() => {
    loadStrokeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport]);

  const loadStrokeTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/ranking-stroke-types?sport=${encodeURIComponent(selectedSport)}`);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 200));
        setError('Server returned an invalid response. Please check if the backend server is running.');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Normalize deception values (Yes/No only) - preserve difficulty as-is for star rating
        const normalizedData = data.data.map((st: any) => {
          // Only normalize deception - preserve difficulty value (can be number or string)
          const normalizeDeception = (val: any): string => {
            if (!val) return '';
            const str = String(val).trim();
            if (str === 'Yes' || str === 'No') {
              return str;
            }
            return ''; // Invalid values
          };
          return {
            ...st,
            // Keep difficulty as-is - it will be normalized when displayed
            difficulty: st.difficulty || '',
            deception: normalizeDeception(st.deception),
          };
        });
        
        // Only log summary info, not individual strokes
        const strokesWithVideos = normalizedData.filter((st: any) => st.referenceVideos && st.referenceVideos.trim().length > 0);
        if (strokesWithVideos.length > 0) {
          console.log(`Loaded ${normalizedData.length} stroke types. ${strokesWithVideos.length} have reference videos.`);
        }
        
        setStrokeTypes(normalizedData);
      } else {
        setError(data.message || 'Failed to load stroke types');
      }
    } catch (err: any) {
      console.error('Error loading stroke types:', err);
      setError(err.message || 'Failed to load stroke types. Please check if the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to normalize deception value (Yes/No)
  const normalizeDeceptionValue = (value: string | undefined | null): string => {
    if (!value) return '';
    const val = String(value).trim();
    // Valid values: 'Yes', 'No'
    if (val === 'Yes' || val === 'No') {
      return val;
    }
    // Convert legacy values or invalid values to empty string
    return '';
  };

  // Helper function to convert difficulty to number (1-5) for star rating
  const normalizeDifficultyValue = (value: string | number | undefined | null): number => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    
    // If already a number, ensure it's 0-5
    if (typeof value === 'number') {
      return Math.max(0, Math.min(5, Math.round(value)));
    }
    
    // If string, try to parse as number first
    const str = String(value).trim();
    const num = parseInt(str, 10);
    if (!isNaN(num) && num >= 0 && num <= 5) {
      return num;
    }
    
    // Legacy string values - convert to numbers
    const lowerStr = str.toLowerCase();
    if (lowerStr === 'low' || lowerStr === '1') return 1;
    if (lowerStr === 'medium' || lowerStr === '2' || lowerStr === '3') return 3;
    if (lowerStr === 'high' || lowerStr === '4' || lowerStr === '5') return 5;
    
    // Default to 0 if can't parse
    return 0;
  };

  const handleOpenDialog = (strokeType?: RankingStrokeType) => {
    if (strokeType) {
      console.log('Opening edit dialog for stroke type:', strokeType);
      setEditingStrokeType(strokeType);
      // Explicitly set all fields with defaults to ensure nothing is undefined
      const formDataToSet = {
        id: strokeType.id,
        code: strokeType.code || '',
        courtPosition: strokeType.courtPosition || '',
        halfSide: strokeType.halfSide || '',
        strokeType: strokeType.strokeType || '',
        strokeAction: strokeType.strokeAction || '',
        playType: strokeType.playType || '',
        shotType: strokeType.shotType || '',
        placement: strokeType.placement || '',
        difficulty: normalizeDifficultyValue(strokeType.difficulty),
        deception: normalizeDeceptionValue(strokeType.deception),
        returnResponse: strokeType.returnResponse || '',
        referenceVideos: strokeType.referenceVideos || '',
        isActive: strokeType.isActive !== undefined ? strokeType.isActive : true,
        displayOrder: strokeType.displayOrder !== undefined ? strokeType.displayOrder : 0,
      };
      console.log('Setting form data:', formDataToSet);
      setFormData(formDataToSet);
      // Parse reference videos from comma-separated string to array
      if (strokeType.referenceVideos) {
        const videos = strokeType.referenceVideos
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0);
        setVideoUrls(videos.length > 0 ? videos : ['']);
      } else {
        setVideoUrls(['']);
      }
    } else {
      setEditingStrokeType(null);
      setFormData({
        code: '',
        courtPosition: '',
        halfSide: '',
        strokeType: '',
        strokeAction: '',
        playType: '',
        shotType: '',
        placement: '',
        difficulty: 0,
        deception: '',
        returnResponse: '',
        referenceVideos: '',
        isActive: true,
        displayOrder: 0,
      });
      setVideoUrls(['']);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStrokeType(null);
    setFormData({
      code: '',
      courtPosition: '',
      halfSide: '',
      strokeType: '',
      strokeAction: '',
      playType: '',
      shotType: '',
      placement: '',
        difficulty: 0,
        deception: '',
      returnResponse: '',
      referenceVideos: '',
      isActive: true,
      displayOrder: 0,
    });
    setVideoUrls(['']);
  };

  const handleSave = async () => {
    if (!formData.code) {
      setError('Code is required');
      return;
    }

    setLoading(true);
    try {
      const url = editingStrokeType?.id
        ? `/api/admin/ranking-stroke-types/${editingStrokeType.id}`
        : '/api/admin/ranking-stroke-types';
      const method = editingStrokeType?.id ? 'PUT' : 'POST';

      // Convert video URLs array to comma-separated string
      const validVideoUrls = videoUrls.filter(v => v.trim().length > 0);
      const referenceVideosString = validVideoUrls.length > 0 ? validVideoUrls.join(',') : '';

      console.log('Saving stroke type - Reference Videos:', referenceVideosString);
      console.log('Video URLs array:', videoUrls);
      console.log('Valid video URLs:', validVideoUrls);

      // Convert difficulty to string for backend (backend stores as TEXT)
      const difficultyValue = typeof formData.difficulty === 'number' 
        ? (formData.difficulty > 0 ? String(formData.difficulty) : '') 
        : (formData.difficulty || '');

      const payload = {
        ...formData,
        sport: selectedSport,
        difficulty: difficultyValue,
        referenceVideos: referenceVideosString,
      };

      console.log('Payload being sent:', payload);

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('Response from server:', data);
      console.log('Response data object:', data.data);
      console.log('Updated referenceVideos in response:', data.data?.referenceVideos);
      console.log('Full response data keys:', data.data ? Object.keys(data.data) : 'no data');
      if (data.data) {
        console.log('All response data values:', {
          code: data.data.code,
          referenceVideos: data.data.referenceVideos,
          returnResponse: data.data.returnResponse,
        });
      }

      if (data.success) {
        const updatedReferenceVideos = data.data?.referenceVideos || '';
        const successMessage = editingStrokeType 
          ? `✅ Stroke type "${formData.code}" updated successfully! Reference Videos: ${updatedReferenceVideos || '(none)'}`
          : '✅ Stroke type created successfully';
        console.log('Setting success message:', successMessage);
        setSuccess(successMessage);
        // Reload stroke types to get updated data BEFORE closing dialog
        console.log('Reloading stroke types after update...');
        setLoading(true);
        await loadStrokeTypes();
        setLoading(false);
        console.log('Stroke types reloaded, closing dialog...');
        handleCloseDialog();
        // Clear success message after 8 seconds (increased for visibility)
        setTimeout(() => {
          console.log('Clearing success message');
          setSuccess(null);
        }, 8000);
      } else {
        setError(data.message || 'Failed to save stroke type');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save stroke type');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVideoUrl = () => {
    setVideoUrls([...videoUrls, '']);
  };

  const handleRemoveVideoUrl = (index: number) => {
    if (videoUrls.length > 1) {
      setVideoUrls(videoUrls.filter((_, i) => i !== index));
    } else {
      setVideoUrls(['']);
    }
  };

  const handleVideoUrlChange = (index: number, value: string) => {
    const newUrls = [...videoUrls];
    newUrls[index] = value;
    setVideoUrls(newUrls);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this stroke type?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/admin/ranking-stroke-types/${id}?sport=${encodeURIComponent(selectedSport)}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Stroke type deleted successfully');
        loadStrokeTypes();
      } else {
        setError(data.message || 'Failed to delete stroke type');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete stroke type');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('Reading Excel file:', file.name, file.size, 'bytes');
      const data = await file.arrayBuffer();
      console.log('File read, size:', data.byteLength);
      
      const workbook = XLSX.read(data, { type: 'array' });
      console.log('Workbook sheets:', workbook.SheetNames);
      
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      if (!worksheet) {
        setError('No worksheet found in Excel file');
        return;
      }

      // Get the range to see what data exists
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      console.log('Worksheet range:', range, 'Rows:', range.e.r + 1, 'Cols:', range.e.c + 1);

      // Parse with explicit options
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        defval: null, // Use null for empty cells (better for filtering)
        raw: false, // Convert all values to strings/numbers
        blankrows: false // Skip blank rows
      });

      console.log('Excel data parsed:', {
        rowCount: jsonData.length,
        firstRow: jsonData[0],
        allKeys: jsonData.length > 0 ? Object.keys(jsonData[0]) : [],
        sampleData: jsonData.slice(0, 3) // First 3 rows
      });

      if (jsonData.length === 0) {
        setError('Excel file appears to be empty or has no data rows. Please check that your file has a header row and at least one data row.');
        return;
      }

      // Validate that we have at least a Code column
      const firstRow = jsonData[0];
      const codeColumnKey = firstRow ? Object.keys(firstRow).find(key => key.toLowerCase() === 'code') : null;
      const hasCode = !!codeColumnKey;

      if (!hasCode) {
        const availableColumns = Object.keys(firstRow).join(', ');
        setError(`Excel file must have a "Code" column. Found columns: ${availableColumns || 'none'}`);
        return;
      }

      // Filter out rows without a code value
      const validRows = jsonData
        .map((row, index) => {
          // Find the code value (case-insensitive)
          const codeKey = Object.keys(row).find(key => key.toLowerCase() === 'code');
          const codeValue = codeKey ? row[codeKey] : null;
          
          // Convert to string and check if it's empty
          // Handle null, undefined, empty string, and string "null"
          let codeStr = '';
          if (codeValue != null && codeValue !== undefined) {
            codeStr = String(codeValue).trim();
            // Also check for string "null" or "undefined"
            if (codeStr.toLowerCase() === 'null' || codeStr.toLowerCase() === 'undefined') {
              codeStr = '';
            }
          }
          
          if (!codeStr) {
            console.log(`Row ${index + 2}: Skipping - no code value. Code value: "${codeValue}", Type: ${typeof codeValue}, Available keys:`, Object.keys(row));
            return null;
          }
          
          // Keep original row structure - backend handles case-insensitive matching
          // Just ensure code field exists for backend compatibility
          const processedRow = { ...row };
          if (codeKey && codeKey !== 'code') {
            // Add lowercase 'code' field while keeping original
            processedRow.code = codeValue;
          }
          
          return processedRow;
        })
        .filter(row => row !== null);

      console.log('Filtered data:', {
        originalCount: jsonData.length,
        validCount: validRows.length,
        filteredOut: jsonData.length - validRows.length,
        sample: validRows[0]
      });

      if (validRows.length === 0) {
        const filteredCount = jsonData.length - validRows.length;
        setError(`No valid rows found. All ${jsonData.length} row(s) are missing a "Code" value. Please ensure your Excel file has a "Code" column with values in each data row (header row is automatically skipped).`);
        return;
      }

      if (validRows.length < jsonData.length) {
        const filteredCount = jsonData.length - validRows.length;
        console.warn(`${filteredCount} row(s) were filtered out because they are missing a "Code" value.`);
      }

      console.log('Sending to backend:', {
        count: validRows.length,
        sample: validRows[0]
      });

      setLoading(true);
      const response = await fetch('/api/admin/ranking-stroke-types/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sport: selectedSport, strokeTypes: validRows }),
      });

      console.log('Backend response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || `HTTP ${response.status}` };
        }
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('Backend result:', result);

      if (result.success) {
        setSuccess(result.message || `Successfully imported ${result.data.length} stroke types`);
        setBulkUploadOpen(false);
        loadStrokeTypes();
      } else {
        setError(result.message || 'Failed to import stroke types');
      }
    } catch (err: any) {
      console.error('Excel upload error:', err);
      setError(err.message || 'Failed to process Excel file. Check browser console for details.');
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      strokeTypes.map((st) => ({
        Code: st.code,
        'Court Position': st.courtPosition || '',
        'Half Court': st.halfSide || '',
        'Stroke Type': st.strokeType || '',
        'Stroke Action': st.strokeAction || '',
        'Play Type': st.playType || '',
        'Shot Type': st.shotType || '',
        Placement: st.placement || '',
        Difficulty: st.difficulty || '',
        Deception: st.deception || '',
        'Return Response': st.returnResponse || '',
        'Reference Videos': st.referenceVideos || '',
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ranking Stroke Types');
    XLSX.writeFile(workbook, `ranking-stroke-types-${selectedSport.toLowerCase()}.xlsx`);
  };

  const handleBack = () => {
    navigate('/admin');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Admin Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            {isFootball ? 'Shooting Skills Assessment' : 'Ranking Stroke Types Management'}
          </Typography>
          <FormControl size="small" sx={{ ml: 2, minWidth: 180 }}>
            <InputLabel>Sport</InputLabel>
            <Select
              value={selectedSport}
              label="Sport"
              onChange={(e) => setSelectedSport(e.target.value)}
            >
              {CORE_SPORTS.map((sport) => (
                <MenuItem key={sport} value={sport}>{sport}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setBulkUploadOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {isFootball ? 'Add Shooting Skill' : 'Add Stroke Type'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            color="error"
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stroke Types List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {isFootball ? `Master Shooting Skills (${strokeTypes.length})` : `Master Ranking Stroke Types (${strokeTypes.length})`}
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadStrokeTypes}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {loading && strokeTypes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: 600,
                border: '4px solid #2196f3',
                borderRadius: '4px',
                overflow: 'auto',
              }}
            >
              <Table stickyHeader sx={{ borderCollapse: 'separate', minWidth: 1400 }}>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      background: '#f1f8e9',
                      backgroundImage: `
                        repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(76, 175, 80, 0.2) 3px, rgba(76, 175, 80, 0.2) 4px)
                      `,
                      borderBottom: '4px solid #2196f3',
                      '& .MuiTableCell-root': {
                        borderBottom: '4px solid #2196f3',
                        borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: '#f1f8e9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Goal Target Zone' : 'Court Position'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', width: '15%', minWidth: '150px' }}>{isFootball ? 'Shooting Position' : 'Half Court'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Player Position' : 'Stroke Type'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Distance (yards)' : 'Stroke Action'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Shooting Method' : 'Play Type'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Area of Improvement' : 'Shot Type'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Possible Pitfalls' : 'Placement'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Shooting Rating' : 'Difficulty'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>{isFootball ? 'Suggested Practices' : 'Return Response'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Reference Videos</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {strokeTypes.map((strokeType) => {
                    // Helper function to get court position cell style and color box props
                    const getCourtPositionStyle = (position: string) => {
                      if (!position) return { style: {}, colorBox: null };
                      const pos = position.toLowerCase().trim();
                      if (pos.includes('backcourt') || pos.includes('back court')) {
                        // Bottom 25% filled
                        return {
                          style: {
                            position: 'relative' as const,
                            padding: '8px',
                          },
                          colorBox: {
                            position: 'absolute' as const,
                            bottom: '4px',
                            left: '4px',
                            right: '4px',
                            height: 'calc(25% - 4px)',
                            backgroundColor: '#66bb6a',
                            borderRadius: '2px',
                            zIndex: 0,
                            pointerEvents: 'none' as const,
                            display: 'block',
                          },
                        };
                      }
                      if (pos.includes('midcourt') || pos.includes('mid court')) {
                        // Center filled
                        return {
                          style: {
                            position: 'relative' as const,
                            padding: '8px',
                          },
                          colorBox: {
                            position: 'absolute' as const,
                            top: '25%',
                            bottom: '25%',
                            left: '4px',
                            right: '4px',
                            backgroundColor: '#66bb6a',
                            borderRadius: '2px',
                            zIndex: 0,
                            pointerEvents: 'none' as const,
                            display: 'block',
                          },
                        };
                      }
                      if (pos.includes('frontcourt') || pos.includes('front court') || pos.startsWith('front')) {
                        // Top half filled
                        return {
                          style: {
                            position: 'relative' as const,
                            padding: '8px',
                            minHeight: '60px',
                          },
                          colorBox: {
                            position: 'absolute' as const,
                            top: '4px',
                            left: '4px',
                            right: '4px',
                            height: 'calc(50% - 4px)',
                            minHeight: '24px',
                            backgroundColor: '#66bb6a', // More visible green
                            borderRadius: '2px',
                            zIndex: 0,
                            pointerEvents: 'none' as const,
                            display: 'block',
                          },
                        };
                      }
                      return { style: {}, colorBox: null };
                    };

                    // Helper function to get color for stroke type
                    const getStrokeTypeColor = (strokeType: string) => {
                      if (!strokeType) return 'transparent';
                      const st = strokeType.toLowerCase();
                      if (st.includes('forehand')) return '#e1f5fe';
                      if (st.includes('backhand')) return '#f3e5f5';
                      if (st.includes('overhead')) return '#fff3e0';
                      if (st.includes('underarm')) return '#e8f5e9';
                      return '#fafafa';
                    };

                    // Helper function to get color for stroke action
                    const getStrokeActionColor = (strokeAction: string) => {
                      if (!strokeAction) return 'transparent';
                      const sa = strokeAction.toLowerCase();
                      if (sa.includes('overhead')) return '#fff9c4';
                      if (sa.includes('underarm')) return '#e0f2f1';
                      if (sa.includes('sidearm')) return '#fce4ec';
                      return '#f5f5f5';
                    };

                    // Helper function to get color for shot type
                    const getShotTypeColor = (shotType: string) => {
                      if (!shotType) return 'transparent';
                      const st = shotType.toLowerCase();
                      const colorMap: { [key: string]: string } = {
                        'smash': '#ffebee',
                        'clear': '#e3f2fd',
                        'drop': '#f1f8e9',
                        'drive': '#fff3e0',
                        'lift': '#f3e5f5',
                        'net': '#e0f2f1',
                        'serve': '#fce4ec',
                        'kill': '#ffcdd2',
                        'block': '#e1bee7',
                        'push': '#c5e1a5',
                      };
                      for (const [key, color] of Object.entries(colorMap)) {
                        if (st.includes(key)) return color;
                      }
                      return '#fafafa';
                    };

                    // Helper function to get half side cell style and color box props
                    const getHalfSideStyle = (halfSide: string) => {
                      if (!halfSide) return { style: {}, colorBox: null };
                      const side = halfSide.toLowerCase();
                      if (side.includes('right')) {
                        return {
                          style: {
                            position: 'relative' as const,
                            padding: '8px',
                          },
                          colorBox: {
                            position: 'absolute' as const,
                            right: '4px',
                            top: '4px',
                            bottom: '4px',
                            width: 'calc(50% - 4px)',
                            backgroundColor: '#66bb6a',
                            borderRadius: '2px',
                            zIndex: 0,
                            pointerEvents: 'none' as const,
                          },
                        };
                      }
                      if (side.includes('left')) {
                        return {
                          style: {
                            position: 'relative' as const,
                            padding: '8px',
                          },
                          colorBox: {
                            position: 'absolute' as const,
                            left: '4px',
                            top: '4px',
                            bottom: '4px',
                            width: 'calc(50% - 4px)',
                            backgroundColor: '#66bb6a',
                            borderRadius: '2px',
                            zIndex: 0,
                            pointerEvents: 'none' as const,
                          },
                        };
                      }
                      return { style: {}, colorBox: null };
                    };

                    const courtPositionData = getCourtPositionStyle(strokeType.courtPosition || '');
                    const halfSideData = getHalfSideStyle(strokeType.halfSide || '');
                    
                    return (
                      <TableRow 
                        key={strokeType.id} 
                        hover
                        sx={{
                          borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                            borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                            '&:last-child': {
                              borderRight: 'none',
                            },
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{strokeType.code}</TableCell>
                        <TableCell 
                          sx={{ 
                            ...courtPositionData.style,
                            fontWeight: 500,
                            position: 'relative',
                            overflow: 'visible',
                          }}
                        >
                          {courtPositionData.colorBox && (
                            <Box 
                              component="div"
                              sx={{
                                ...courtPositionData.colorBox,
                              }}
                            />
                          )}
                          <Box 
                            component="span"
                            sx={{ 
                              position: 'relative', 
                              zIndex: 1,
                              display: 'inline-block',
                              width: '100%',
                            }}
                          >
                            {strokeType.courtPosition || '-'}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            ...halfSideData.style,
                            fontWeight: 500,
                            position: 'relative',
                            verticalAlign: 'middle',
                            width: '15%',
                            minWidth: '150px',
                          }}
                        >
                          {halfSideData.colorBox && (
                            <Box 
                              component="div"
                              sx={{
                                ...halfSideData.colorBox,
                              }}
                            />
                          )}
                          <Box 
                            component="span"
                            sx={{ 
                              position: 'relative', 
                              zIndex: 1,
                              display: 'inline-block',
                              width: '100%',
                            }}
                          >
                            {(() => {
                              const halfSide = strokeType.halfSide || '';
                              if (!halfSide) return '-';
                              // Normalize display: "Right Service Court" -> "Right Side", "Left Service Court" -> "Left Side"
                              if (halfSide.toLowerCase().includes('right service court')) {
                                return 'Right Side';
                              }
                              if (halfSide.toLowerCase().includes('left service court')) {
                                return 'Left Side';
                              }
                              // If already "Right Side" or "Left Side", return as is
                              if (halfSide.toLowerCase().includes('right side')) {
                                return 'Right Side';
                              }
                              if (halfSide.toLowerCase().includes('left side')) {
                                return 'Left Side';
                              }
                              // Fallback: if contains "right" or "left", normalize to "Right Side" or "Left Side"
                              if (halfSide.toLowerCase().includes('right')) {
                                return 'Right Side';
                              }
                              if (halfSide.toLowerCase().includes('left')) {
                                return 'Left Side';
                              }
                              return halfSide;
                            })()}
                          </Box>
                        </TableCell>
                      <TableCell sx={{ bgcolor: getStrokeTypeColor(strokeType.strokeType || ''), verticalAlign: 'middle' }}>
                        {strokeType.strokeType || '-'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: getStrokeActionColor(strokeType.strokeAction || ''), verticalAlign: 'middle' }}>
                        {strokeType.strokeAction || '-'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>{strokeType.playType || '-'}</TableCell>
                      <TableCell sx={{ bgcolor: getShotTypeColor(strokeType.shotType || ''), verticalAlign: 'middle' }}>
                        {strokeType.shotType || '-'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>{strokeType.placement || '-'}</TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {(() => {
                          const difficultyValue = normalizeDifficultyValue(strokeType.difficulty);
                          // Always show rating component, even if 0
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '120px' }}>
                              <Rating 
                                value={difficultyValue} 
                                max={5} 
                                readOnly 
                                size="small"
                              />
                              {difficultyValue > 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  ({difficultyValue}/5)
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                                  (Not rated)
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>{strokeType.returnResponse || '-'}</TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {strokeType.referenceVideos && strokeType.referenceVideos.trim().length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {strokeType.referenceVideos
                              .split(',')
                              .map((url) => url.trim())
                              .filter((url) => url.length > 0)
                              .map((url, idx) => (
                                <VideoPlayer
                                  key={idx}
                                  url={url}
                                  videoNumber={idx + 1}
                                />
                              ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            No videos
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Chip
                          label={strokeType.isActive !== false ? 'Active' : 'Inactive'}
                          size="small"
                          color={strokeType.isActive !== false ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(strokeType)}
                              disabled={loading}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => strokeType.id && handleDelete(strokeType.id)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStrokeType ? (isFootball ? 'Edit Shooting Skill' : 'Edit Stroke Type') : (isFootball ? 'Add New Shooting Skill' : 'Add New Stroke Type')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code *"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
                helperText="Unique identifier (editable)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Goal Target Zone' : 'Court Position'}
                value={formData.courtPosition || ''}
                onChange={(e) => setFormData({ ...formData, courtPosition: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Shooting Position' : 'Half Court'}
                value={formData.halfSide || ''}
                onChange={(e) => setFormData({ ...formData, halfSide: e.target.value })}
                helperText={isFootball ? 'Ex: Left foot, Right foot, Volley, Header setup' : "Enter 'Right Side' or 'Left Side' (or 'Right Service Court'/'Left Service Court' for legacy support)"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Player Position' : 'Stroke Type'}
                value={formData.strokeType || ''}
                onChange={(e) => setFormData({ ...formData, strokeType: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Distance (yards)' : 'Stroke Action'}
                value={formData.strokeAction || ''}
                onChange={(e) => setFormData({ ...formData, strokeAction: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Shooting Method' : 'Play Type'}
                value={formData.playType || ''}
                onChange={(e) => setFormData({ ...formData, playType: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Area of Improvement' : 'Shot Type'}
                value={formData.shotType || ''}
                onChange={(e) => setFormData({ ...formData, shotType: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isFootball ? 'Possible Pitfalls' : 'Placement'}
                value={formData.placement || ''}
                onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {isFootball ? 'Shooting Rating' : 'Difficulty'}
                </Typography>
                <Rating
                  value={typeof formData.difficulty === 'number' ? formData.difficulty : normalizeDifficultyValue(formData.difficulty)}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, difficulty: newValue || 0 });
                  }}
                  max={5}
                  size="large"
                />
                {formData.difficulty > 0 && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({formData.difficulty}/5)
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Deception</InputLabel>
                <Select
                  value={formData.deception || ''}
                  label="Deception"
                  onChange={(e) => setFormData({ ...formData, deception: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isFootball ? 'Suggested Practices' : 'Return Response'}
                value={formData.returnResponse || ''}
                onChange={(e) => setFormData({ ...formData, returnResponse: e.target.value })}
                multiline
                rows={2}
                helperText={isFootball ? 'Recommended drills or corrective practices' : 'Expected return response for this stroke type'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Reference Videos
              </Typography>
              {videoUrls.map((url, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label={`Video ${index + 1} URL`}
                    value={url}
                    onChange={(e) => handleVideoUrlChange(index, e.target.value)}
                    placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                    helperText={index === 0 ? "YouTube links or direct video URLs (mp4, webm, etc.)" : ""}
                  />
                  {videoUrls.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveVideoUrl(index)}
                      sx={{ mt: 1 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddVideoUrl}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Add Video URL
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive !== false ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.displayOrder || 0}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                helperText="Lower numbers appear first"
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : editingStrokeType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isFootball ? 'Bulk Upload Shooting Skills' : 'Bulk Upload Ranking Stroke Types'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload an Excel file (.xlsx) with the following columns:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 1 }}>
              <li>Code (required) - Unique identifier</li>
              <li>Court Position</li>
              <li>Half Court</li>
              <li>{isFootball ? 'Player Position' : 'Stroke Type'}</li>
              <li>Stroke Action</li>
              <li>Play Type</li>
              <li>Shot Type</li>
              <li>Placement</li>
              <li>Difficulty (1-5 star rating)</li>
              <li>Deception (Yes/No)</li>
              <li>Return Response</li>
              <li>Reference Videos (comma-separated URLs)</li>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              If a stroke type with the same code already exists, it will be updated. Otherwise, a new one will be created.
            </Alert>
            <Box sx={{ mt: 3 }}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="excel-upload-input"
                type="file"
                onChange={handleExcelUpload}
              />
              <label htmlFor="excel-upload-input">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Choose Excel File'}
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminRankingStrokeTypesManagement;

